import { Location, NavigateFunction } from 'react-router-dom'

import { AppInstanceInfo, AppProperties } from 'common/types/application'
import { AnalysisResponse } from 'common/types/applicationAnalysis'
import {
  AppVersionItem,
  CreateAppVersionParams,
  SaveAppInNewVersionParams,
  SaveAppVersionInAnotherAppParams,
  UpdateAppVersionParams
} from 'common/types/version'
import {
  AppActionTypes,
  AssetsRecoveryDialogState,
  OpenAppParams,
  OpenNotificationsRequest,
  TasksBoardState
} from './types'
import { DataToCompare } from 'pages/appConsolidation/types'
import { MyDepartmentTeam } from 'common/types/team'

export const openApp = (params: OpenAppParams) => {
  return {
    type: AppActionTypes.OPEN_APP,
    payload: {
      fullPath: params.fullPath,
      navigate: params.navigate,
      openAsReadOnly: params.openAsReadOnly,
      version: params.version,
      resources: params.resources,
      forceNewInstance: params.forceNewInstance,
      save: params.save,
      saveMsg: params.saveMsg,
      interfaceId: params.interface,
      redirectTo: params.redirectTo,
      attemptRecovery: params.attemptRecovery
    }
  }
}

export const openAppOnNewInstance = (params: OpenAppParams) => {
  return {
    type: AppActionTypes.OPEN_APP_ON_NEW_INSTANCE,
    payload: {
      fullPath: params.fullPath,
      navigate: params.navigate,
      openAsReadOnly: params.openAsReadOnly,
      version: params.version,
      resources: params.resources,
      interfaceId: params.interface
    }
  }
}

export const createVersionFromClosedApp = (
  navigate: NavigateFunction,
  folder: string,
  dataToSend: CreateAppVersionParams,
  openOnNewInstance?: boolean
) => {
  return {
    type: AppActionTypes.CREATE_VERSION_FROM_CLOSED_APP,
    payload: {
      navigate,
      folder,
      dataToSend,
      openOnNewInstance
    }
  }
}

export const closeApp = (
  navigate: NavigateFunction,
  successMsg: string,
  save?: boolean,
  saveMsg?: string
) => {
  return {
    type: AppActionTypes.CLOSE_APP,
    payload: { navigate, successMsg, save, saveMsg }
  }
}

export const setOpenApp = (applicationInfo?: AppInstanceInfo) => {
  return {
    type: AppActionTypes.SET_OPEN_APP,
    payload: applicationInfo
  }
}

export const resetAppInfo = () => {
  return {
    type: AppActionTypes.RESET_APP_INFO
  }
}

export const saveApp = (
  confirmNodeDefinitions: boolean,
  successMsg: string,
  openApp?: { open: boolean; openInNewInstance: boolean; modelPath: string },
  savedFromOpenApp?: boolean
) => {
  return {
    type: AppActionTypes.SAVE_APP,
    payload: { confirmNodeDefinitions, successMsg, openApp, savedFromOpenApp }
  }
}
export const setOpenAppAfterSave = (value?: {
  open: boolean
  openInNewInstance: boolean
  modelPath: string
  save?: boolean
}) => {
  return {
    type: AppActionTypes.SET_OPEN_APP_AFTER_SAVE,
    payload: value
  }
}
export const saveAppInMyWorkspaceOrTeam = (
  confirmNodeDefinitions: boolean,
  successMsg: string,
  newPathMsg: string,
  folderName: string,
  versions?: string[],
  navigate?: NavigateFunction,
  teamFolder?: string
) => {
  return {
    type: AppActionTypes.SAVE_APP_IN_WORKSPACE_OR_TEAM,
    payload: {
      confirmNodeDefinitions,
      successMsg,
      newPathMsg,
      folderName,
      versions,
      navigate,
      teamFolder
    }
  }
}

export const saveAppAsNewVersion = (
  data: SaveAppInNewVersionParams,
  navigate?: NavigateFunction,
  preventNavigate?: boolean,
  getConsolidationDifferenceData?: object
) => {
  return {
    type: AppActionTypes.SAVE_APP_AS_NEW_VERSION,
    payload: {
      new_version_name: data.new_version_name,
      labels: data.labels,
      set_as_default: data.set_as_default,
      version_description: data.version_description,
      technical_version: data.technical_version,
      navigate,
      preventNavigate,
      getConsolidationDifferenceData
    }
  }
}

export const saveAppVersionInAnotherApp = (
  data: SaveAppVersionInAnotherAppParams,
  navigate?: NavigateFunction
) => {
  return {
    type: AppActionTypes.SAVE_APP_VERSION_IN_ANOTHER_APP,
    payload: {
      app_path: data.app_path,
      new_version_name: data.new_version_name,
      labels: data.labels,
      set_as_default: data.set_as_default,
      version_description: data.version_description,
      technical_version: data.technical_version,
      navigate
    }
  }
}

export const setAppConsolidationDifferences = (data?: DataToCompare) => {
  return {
    type: AppActionTypes.GET_APP_CONSOLIDATION_DIFFERENCES,
    payload: data
  }
}

export const setSavingApp = (saving: boolean) => {
  return {
    type: AppActionTypes.SET_SAVING_APP,
    payload: saving
  }
}
export const setClosingApp = (value?: boolean) => {
  return {
    type: AppActionTypes.CLOSING_APP,
    payload: value
  }
}

export const setAppHasUnsavedChanges = (hasUnsavedChanges: boolean) => {
  return {
    type: AppActionTypes.SET_APP_HAS_UNSAVED_CHANGES,
    payload: hasUnsavedChanges
  }
}

export const showCreateAppDialog = (show: boolean) => {
  return {
    type: AppActionTypes.SHOW_CREATE_APP_DIALOG,
    payload: show
  }
}

export const createApp = (
  name: string,
  template: string,
  onPublic: boolean,
  pythonVersion: string,
  navigate: NavigateFunction,
  currentTeamTab?: MyDepartmentTeam,
  commonTabName?: string,
  currentAppFolder?: string
) => {
  return {
    type: AppActionTypes.CREATE_APP,
    payload: {
      name,
      template,
      onPublic,
      pythonVersion,
      navigate,
      currentTeamTab,
      commonTabName,
      currentAppFolder
    }
  }
}

export const setAppInfo = (appInfo: AppInstanceInfo | undefined) => {
  return {
    type: AppActionTypes.SET_APP_INFO,
    payload: appInfo
  }
}

export const updateDefaultInterfaceId = (interfaceId: string) => {
  return {
    type: AppActionTypes.UPDATE_DEFAULT_INTERFACE_ID,
    payload: interfaceId
  }
}

export const setAppReloaded = (value?: boolean) => {
  return {
    type: AppActionTypes.APP_RELOADED,
    payload: value
  }
}

export const reloadWithMyVenv = (message: string) => {
  return {
    type: AppActionTypes.RELOAD_WITH_MY_VENV,
    payload: { message }
  }
}

export const downloadExample = (name: string, navigate: NavigateFunction) => {
  return {
    type: AppActionTypes.DOWNLOAD_EXAMPLE,
    payload: { name, navigate }
  }
}

export const changeAppVersion = (version: string, navigate: NavigateFunction) => {
  return {
    type: AppActionTypes.CHANGE_APP_VERSION,
    payload: {
      version,
      navigate
    }
  }
}

export const updateAppVersionProperties = (
  version: string,
  newProperties: UpdateAppVersionParams,
  navigate: NavigateFunction,
  location: Location,
  applicationLanguage?: string
) => {
  return {
    type: AppActionTypes.UPDATE_APP_VERSION_PROPERTIES,
    payload: {
      version,
      newProperties,
      navigate,
      location,
      applicationLanguage
    }
  }
}

export const setChangingAppVersion = (changingAppVersion: boolean) => {
  return {
    type: AppActionTypes.SET_CHANGING_APP_VERSION,
    payload: changingAppVersion
  }
}
export const setCreatingAppVersion = (creatingAppVersion: boolean) => {
  return {
    type: AppActionTypes.SET_CREATING_APP_VERSION,
    payload: creatingAppVersion
  }
}
export const setHasProcessDelayed = (value?: boolean) => {
  return {
    type: AppActionTypes.HAS_PROCESS_DELAYED,
    payload: value
  }
}
/**
 * Ask again whether the user is in an active process.
 *
 * Dispatched whenever something happens in a process — the answer changes
 * without this session doing anything: somebody else starts a process, or puts
 * this person on a task.
 */
export const checkHasActiveProcess = () => {
  return {
    type: AppActionTypes.CHECK_HAS_ACTIVE_PROCESS
  }
}
export const setHasActiveProcess = (value?: boolean) => {
  return {
    type: AppActionTypes.SET_HAS_ACTIVE_PROCESS,
    payload: value
  }
}
/** Remember how the task board was left, so coming back lands where it was. */
export const setTasksBoardView = (value: TasksBoardState) => {
  return {
    type: AppActionTypes.SET_TASKS_BOARD_VIEW,
    payload: value
  }
}
export const setAppVersionsDialog = (
  showAppVersionsDialog: boolean,
  folder?: string,
  defaultVersion?: AppVersionItem,
  openAsReadOnly?: boolean,
  openOnNewInstance?: boolean,
  appProperties?: AppProperties,
  canWriteInDir?: boolean,
  resources?: string,
  interfaceId?: string,
  openWithOptions?: boolean
) => {
  return {
    type: AppActionTypes.SHOW_APP_VERSIONS_DIALOG,
    payload: {
      show: showAppVersionsDialog,
      folder,
      defaultVersion,
      appProperties,
      openAsReadOnly,
      openOnNewInstance: openOnNewInstance === undefined ? false : openOnNewInstance,
      canWriteInDir,
      resources,
      interfaceId,
      openWithOptions
    }
  }
}
export const refreshScenariosTable = () => {
  return {
    type: AppActionTypes.REFRESH_SCENARIOS_TABLE
  }
}

export const addBroadcastChannel = (channelId: string) => {
  return {
    type: AppActionTypes.ADD_BROADCAST_CHANNEL,
    payload: channelId
  }
}

export const removeBroadcastChannel = () => {
  return {
    type: AppActionTypes.REMOVE_BROADCAST_CHANNEL
  }
}
export const setOpenNotifications = (value?: OpenNotificationsRequest) => {
  return {
    type: AppActionTypes.OPEN_NOTIFICATIONS,
    payload: value
  }
}
export const setNotificationsBellMounted = (value: boolean) => {
  return {
    type: AppActionTypes.SET_NOTIFICATIONS_BELL_MOUNTED,
    payload: value
  }
}
export const setShowUnreadNotifications = (value?: boolean) => {
  return {
    type: AppActionTypes.SHOW_UNREAD_NOTIFICATIONS,
    payload: value
  }
}
export const setSavingAppWithInterfaceEditMode = (value?: boolean) => {
  return {
    type: AppActionTypes.SET_SAVING_APP_WITH_INTERFACE_EDIT_MODE,
    payload: value
  }
}
export const setOpenSaveAppDialogFromInterface = (value?: boolean) => {
  return {
    type: AppActionTypes.SET_OPEN_SAVE_APP_DIALOG_FROM_INTERFACE,
    payload: value
  }
}
export const setWaiting = (value?: boolean) => {
  return {
    type: AppActionTypes.SET_WAITING,
    payload: value
  }
}
export const closeAppFromOtherTab = (
  navigate: NavigateFunction,
  message: string = 'app_closed_in_another_tab'
) => {
  return {
    type: AppActionTypes.CLOSE_APP_FROM_OTHER_TAB,
    payload: { navigate, message }
  }
}

export const setAnalysisResult = (applicationAnalysisResult: AnalysisResponse) => {
  return {
    type: AppActionTypes.SET_ANALYSIS_RESULT,
    payload: applicationAnalysisResult
  }
}

export const cleanAnalysisResult = () => {
  return {
    type: AppActionTypes.CLEAN_ANALYSIS_RESULT
  }
}
export const setAssetsRecoveryDialog = (dialog: AssetsRecoveryDialogState | null) => {
  return {
    type: AppActionTypes.SET_ASSETS_RECOVERY_DIALOG,
    payload: dialog
  }
}
export const setOpeningAppName = (appName?: string) => {
  return {
    type: AppActionTypes.SET_OPENING_APP_NAME,
    payload: appName
  }
}
export const setAppSavedFromOpenApp = (value?: boolean) => {
  return {
    type: AppActionTypes.APP_SAVED_FROM_OPEN_APP,
    payload: value
  }
}

export const setAppHasDocumentation = (appHasDocumentation: boolean) => {
  return {
    type: AppActionTypes.SET_APP_HAS_DOCUMENTATION,
    payload: appHasDocumentation
  }
}
export const setCurrentTeamTab = (currentTeamTab: MyDepartmentTeam | null) => {
  return {
    type: AppActionTypes.SET_CURRENT_TEAM_TAB,
    payload: currentTeamTab
  }
}

export const setCommonTabName = (commonTabName: string) => {
  return {
    type: AppActionTypes.SET_COMMON_TAB_NAME,
    payload: commonTabName
  }
}

export const setCurrentAppFolder = (currentAppFolder: string) => {
  return {
    type: AppActionTypes.SET_CURRENT_APP_FOLDER,
    payload: currentAppFolder
  }
}

export const cancelOpenApp = (navigate: NavigateFunction) => {
  return {
    type: AppActionTypes.CANCEL_OPEN_APP,
    payload: { navigate }
  }
}

export const setCancelOpenAppSuccess = () => {
  return {
    type: AppActionTypes.CANCEL_OPEN_APP_SUCCESS
  }
}

export const setCancelingOpenApp = (value?: boolean) => {
  return {
    type: AppActionTypes.CANCELING_OPEN_APP,
    payload: value
  }
}

export const forceStopAndReopen = (params: {
  navigate: NavigateFunction
  fullPath: string
  moduleId?: string
  selectedNodeId?: string
  withSave?: boolean
}) => {
  return {
    type: AppActionTypes.FORCE_STOP_AND_REOPEN,
    payload: params
  }
}
